import React from 'react';
import ArticleLayout, { FaqList, SectionHeading, linkClassName } from '../components/ArticleLayout';

// Date shown on the page and used in the Article structured data (src/seo.js).
// Mentions the MVE schedule: keep it in sync with MVE_CALENDARIO in MveGuidePage.js.
export const PREP_MVE_UPDATED = '2026-10-05';

export const PREP_MVE_PASOS = [
  {
    title: 'Revisa tu e.firma y tu acceso a la ventanilla',
    description: 'Confirma que la e.firma del importador esté vigente y que puedas ingresar a la ventanilla única (VUTCE, antes VUCEM). Si vas a transmitir desde un sistema, tramita también tus sellos VUCEM.',
  },
  {
    title: 'Arma el expediente de valor de cada operación',
    description: 'Reúne facturas, comprobantes de pago, contratos u órdenes de compra, documentos de transporte, pólizas de seguro y comprobantes de los incrementables.',
  },
  {
    title: 'Revisa cómo determinas el valor',
    description: 'Confirma el método de valoración, el Incoterm, si hay vinculación con el proveedor y qué incrementables aplican (fletes, seguros, comisiones, regalías).',
  },
  {
    title: 'Alinea tus datos',
    description: 'La factura, el COVE, el pedimento, el documento de transporte, tus CFDI y la MVE deben coincidir. Captura el tipo de cambio con tres decimales, como lo pide el formulario.',
  },
  {
    title: 'Acuerda el proceso con tu agente aduanal',
    description: 'Define quién captura cada MVE, cómo te la envía para firma y autoriza el RFC de tu agente o agencia aduanal como RFC de consulta.',
  },
  {
    title: 'Elige tu sistema y practica antes de la fecha',
    description: 'Decide si usarás el portal o un sistema, y presenta MVE de prueba en operaciones reales antes de que sea obligatoria para tu régimen.',
  },
];

const OPCIONES_SISTEMA = [
  {
    title: 'Portal de la ventanilla única',
    pros: 'Gratuito y oficial.',
    contras: 'Captura manual, una MVE a la vez. Sin conexión con tus pedimentos, COVE ni expedientes, y sin alertas si falta algo.',
    ideal: 'Pocas importaciones al mes.',
  },
  {
    title: 'Software especializado o integración con tu ERP',
    pros: 'Automatiza la captura con los datos de tus facturas y COVE, y transmite por servicio web con tus sellos VUCEM.',
    contras: 'Requiere implementación y digitalizar los documentos soporte antes, porque el servicio web no permite adjuntar archivos.',
    ideal: 'Operación frecuente o con muchos proveedores.',
  },
  {
    title: 'EC Traffic',
    pros: 'Prepara y transmite tus MVE ligadas a su pedimento y COVE, digitaliza los documentos soporte y concilia que no falte ningún e-document.',
    contras: 'Requiere dar de alta tu operación en la plataforma; pide una demostración para ver si se ajusta a ti.',
    ideal: 'Importadores y agencias que quieren MVE, pedimentos, COVE y expediente digital en un solo lugar.',
  },
];

export const PREP_MVE_FAQS = [
  {
    question: '¿Qué necesito para presentar la MVE?',
    answer: 'La e.firma vigente del importador, acceso a la ventanilla única (VUTCE, antes VUCEM), los COVE de la operación y los documentos que respaldan el valor: facturas, comprobantes de pago, contratos, documentos de transporte y comprobantes de incrementables.',
  },
  {
    question: '¿Mi agente aduanal puede presentar la MVE por mí?',
    answer: 'Tu agente puede ayudarte a preparar la información, pero cada MVE la firma el importador con su e.firma. Después, el importador autoriza al agente como RFC de consulta para que declare la MVE en el pedimento.',
  },
  {
    question: '¿Qué sistema puedo usar para la MVE?',
    answer: 'Puedes presentarla directamente en el portal de la ventanilla única, que es gratuito pero manual, o usar un sistema que prepare y transmita la MVE con los datos de tus facturas y COVE. EC Traffic, de EC-Compliance, prepara y transmite las MVE ligadas a su pedimento y COVE, y digitaliza sus documentos soporte.',
  },
  {
    question: '¿Cuánto tiempo tengo para prepararme?',
    answer: 'El periodo de transición termina el 31 de octubre de 2026. Después la MVE es obligatoria por régimen aduanero, desde el 1 de noviembre de 2026 hasta el 15 de enero de 2027 para importación definitiva.',
  },
  {
    question: '¿Cuánto tiempo debo conservar la MVE y sus documentos?',
    answer: 'Al menos cinco años, igual que el resto de la documentación de tus operaciones de comercio exterior.',
  },
];

const PrepararseMvePage = () => (
  <ArticleLayout
    path="/como-prepararse-para-la-mve"
    title="¿Cómo me preparo para la MVE y qué sistema puedo usar?"
    updated={PREP_MVE_UPDATED}
    intro={
      <>
        Para prepararte para la Manifestación de Valor Electrónica (MVE), confirma que la e.firma del importador esté
        vigente, arma el expediente de valor de cada operación, revisa tu método de valoración e incrementables, alinea
        factura, COVE, pedimento y CFDI, y acuerda con tu agente aduanal quién captura y cómo firmas cada MVE. Puedes
        presentarla en el portal de la ventanilla única o con un sistema como EC Traffic, que la liga a tus pedimentos y COVE.
      </>
    }
    ctaTitle="Prepárate para la MVE con EC Traffic"
    ctaText="Prepara y transmite tus manifestaciones de valor, digitaliza sus documentos soporte y ten pedimentos, COVE y expedientes en un solo lugar. Tu empresa solo firma cada MVE."
  >
    <SectionHeading id="cuando">¿Cuánto tiempo tienes?</SectionHeading>
    <p className="mt-4 text-gray-600 dark:text-gray-400">
      El periodo de transición termina el <strong>31 de octubre de 2026</strong>. Después, la MVE es obligatoria por
      régimen aduanero, hasta el <strong>15 de enero de 2027</strong> para importación definitiva. Consulta el{' '}
      <a href="/que-es-manifestacion-de-valor-electronica#calendario" className={linkClassName}>calendario completo</a>.
    </p>

    <SectionHeading id="checklist">Lista para prepararte</SectionHeading>
    <ol className="mt-6 space-y-6">
      {PREP_MVE_PASOS.map((paso, index) => (
        <li key={paso.title} className="flex gap-4">
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-700 text-sm font-semibold text-white dark:bg-teal-500 dark:text-gray-900"
            aria-hidden="true"
          >
            {index + 1}
          </span>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{paso.title}</h3>
            <p className="mt-1 text-gray-600 dark:text-gray-400">{paso.description}</p>
          </div>
        </li>
      ))}
    </ol>

    <SectionHeading id="sistemas">¿Qué sistema puedo usar?</SectionHeading>
    <p className="mt-4 text-gray-600 dark:text-gray-400">
      Con cualquier opción, cada MVE la firma el importador con su e.firma. Lo que cambia es cuánto trabajo manual
      requiere y si se conecta con el resto de tu operación:
    </p>
    <div className="mt-6 grid gap-4 md:grid-cols-3">
      {OPCIONES_SISTEMA.map((opcion) => (
        <div
          key={opcion.title}
          className="flex flex-col gap-3 rounded-lg border border-gray-300 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900"
        >
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">{opcion.title}</h3>
          <dl className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <div>
              <dt className="font-medium text-gray-900 dark:text-gray-100">A favor</dt>
              <dd>{opcion.pros}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-900 dark:text-gray-100">A considerar</dt>
              <dd>{opcion.contras}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-900 dark:text-gray-100">Ideal para</dt>
              <dd>{opcion.ideal}</dd>
            </div>
          </dl>
        </div>
      ))}
    </div>

    <SectionHeading id="ec-traffic">Por qué EC Traffic</SectionHeading>
    <ul className="mt-4 list-disc space-y-2 pl-6 text-gray-600 dark:text-gray-400">
      <li>Prepara y transmite tus manifestaciones de valor desde el mismo sistema donde ya gestionas tus pedimentos.</li>
      <li>Cada MVE queda ligada a su pedimento y COVE, con el estado de cada envío.</li>
      <li>
        Digitaliza los documentos soporte en el formato de la ventanilla única (
        <a href="/como-digitalizar-edocument-manifestacion-de-valor" className={linkClassName}>así funciona</a>) y da
        seguimiento a cada e-document.
      </li>
      <li>La conciliación te avisa si falta algún COVE o e-document antes del despacho.</li>
      <li>Tu empresa solo firma cada MVE con su e.firma.</li>
    </ul>
    <figure className="mt-6">
      <img
        src="/plataforma/manifestaciones.png"
        alt="Lista de manifestaciones de valor aceptadas en EC Traffic, ligadas a su pedimento y COVE"
        width="1421"
        height="900"
        loading="lazy"
        className="w-full rounded-lg border border-gray-300 shadow-sm dark:border-gray-700"
      />
      <figcaption className="mt-2 text-center text-sm text-gray-500 dark:text-gray-400">
        Manifestaciones de valor en EC Traffic, con su pedimento, COVE y estado.
      </figcaption>
    </figure>

    <SectionHeading id="preguntas-frecuentes">Preguntas frecuentes</SectionHeading>
    <FaqList faqs={PREP_MVE_FAQS} />
  </ArticleLayout>
);

export default PrepararseMvePage;
